import React, { useState, useEffect, useMemo, useContext } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { Box, Typography } from "@mui/material";
import StyledModal from "../../Modals/StyledModal/StyledModal";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CustomizedTable from "../../CustomizedTable/CustomizedTable";
import { DefaultCell } from "../../CustomizedTable/CustomCells/CustomCells";
import ShotClockTimerCell from "../../CustomizedTable/CustomCells/ShotClockTimerCell/ShotClockTimerCell";
import PrimaryButton from "../../Buttons/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../Buttons/SecondaryButton/SecondaryButton";
import useShotClockTimer from "../../../hooks/useShotClockTimer/useShotClockTimer";
import { formatTitleCase } from "../../../utils/formatHelpers";
import { handleAltEnter } from "../../../utils/keyboardShortcutHelpers";
import { ShotClockService } from "../../../services/ShotClockService";
import { AlertContext, ActiveUserContext } from "../../Dashboard/Dashboard";

export default function ShotClockQueueModal({
  isOpen,
  setIsOpen,
  load,
  expirationTime,
  loadLocked
}) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const activeUser = useContext(ActiveUserContext);
  const [queuedUsers, setQueuedUsers] = useState([]);
  const [activeUserInQueue, setActiveUserInQueue] = useState(false);
  const [userLocked, setUserLocked] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const { timeRemaining } = useShotClockTimer(loadLocked, expirationTime);

  const users = useMemo(() => queuedUsers, [queuedUsers]);

  const columns = useMemo(
    () =>
      [
        {
          Header: "Position",
          accessor: "sequence",
          filter: "fuzzyText",
          width: 100,
          Cell: DefaultCell,
        },
        {
          Header: "Representative",
          accessor: "userId",
          filter: "fuzzyText",
          width: 140,
          Cell: DefaultCell,
        },
        {
          Header: "Time Remaining",
          id: "lockExpiration",
          timeRemaining: timeRemaining,
          width: 100,
          Cell: ShotClockTimerCell,
        },
      ],
    [timeRemaining]
  );

  const getLocks = () => {
    const lockedUser = {
      userId: loadLocked.lockedUserId,
      sequence: 0
    };

    setSubmitDisabled(true);
    ShotClockService.GetPendingLocks(instance, load.movementId).then(res => {
      res.unshift(lockedUser);
      res.forEach(r => r.sequence = r.sequence + 1);
      setQueuedUsers(res);
      setSubmitDisabled(false);
      setIsLoading(false);
    }).catch(error => {
      console.error(error);
      setSubmitDisabled(false);
      setIsLoading(false);
      warning(error);
    });
  };

  const handleSubmit = () => {
    setSubmitDisabled(true);
    setIsLoading(true);
    if (userLocked) {
      ShotClockService.UnlockLoad(instance, load.movementId).then(() => {
        setIsOpen(false);
      }).catch(error => {
        console.error(error);
        setSubmitDisabled(false);
        setIsLoading(false);
        warning(error);
      });
    } else if (activeUserInQueue) {
      ShotClockService.RemovePendingLock(instance, load.movementId, activeUser.userIdMcld).then(() => {
        getLocks();
      }).catch(error => {
        console.error(error);
        setSubmitDisabled(false);
        setIsLoading(false);
        warning(error);
      });
    } else {
      ShotClockService.AddPendingLock(instance, load.movementId, activeUser.userIdMcld).then(() => {
        getLocks();
      }).catch(error => {
        console.error(error);
        setSubmitDisabled(false);
        setIsLoading(false);
        warning(error);
      });
    }
  };

  useEffect(() => {
    setActiveUserInQueue(!!queuedUsers.find(qr => qr.userId === activeUser.userIdMcld));
  }, [queuedUsers, activeUser]);

  useEffect(() => {
    if (loadLocked) {
      setUserLocked(loadLocked.lockedUserId === activeUser?.userIdMcld);
    }
  }, [loadLocked, activeUser]);

  useEffect(() => {
    if (isOpen) {
      getLocks();
    }
  }, [isOpen, loadLocked]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      handleAltEnter(e, submitDisabled, handleSubmit);
    };

    document.addEventListener("keydown", handleKeyDown);

    return function cleanup() {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [submitDisabled]);

  return (
    <StyledModal
      label="queue-for-locked-load"
      title="Queue for Locked Load"
      open={isOpen}
      handleClose={() => setIsOpen(false)}
    >
      <Box sx={{ width: "600px", height: "400px" }}>
        <Box sx={{ display: "flex", alignItems: "center", marginBottom: "30px" }}>
          <Typography variant="h2" color="primary.60">
            #{load.orderId ?? load.id}
          </Typography>
          <Typography variant="h2" color="black_solid.80" sx={{ marginLeft: "20px" }}>
            {formatTitleCase(load.originCity).trim()}, {load.originState}
          </Typography>
          <ArrowForwardIcon sx={{ marginLeft: "10px", color: "black_solid.60" }} />
          <Typography variant="h2" color="black_solid.80" sx={{ marginLeft: "10px" }}>
            {formatTitleCase(load.destinationCity).trim()}, {load.destinationState}
          </Typography>
        </Box>
        <CustomizedTable
          id="shot-clock-queued-users"
          data={users}
          columns={columns}
          defaultHeight={230}
          defaultWidth={600}
        />
        <Box sx={{ display: "flex", marginTop: "20px" }}>
          <Box sx={{ margin: "0 20px 0 auto" }}>
            <SecondaryButton
              label="Cancel"
              onClick={() => setIsOpen(false)}
              disabled={isLoading}
            />
          </Box>
          <PrimaryButton
            label={userLocked ? "Unlock Load" : activeUserInQueue ? "Remove from Queue" : "Add to Queue"}
            width="160px"
            onClick={handleSubmit}
            disabled={submitDisabled}
            isLoading={isLoading}
          />
        </Box>
      </Box>
    </StyledModal>
  );
}

ShotClockQueueModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  load: PropTypes.object,
  expirationTime: PropTypes.string,
  loadLocked: PropTypes.object
};